import { apiClient } from './apiClient';

export type CacheScopeSummary = {
  scope: string;
  moduleCode: string;
  label: string;
  customerId: string;
  entryCount: number;
  lastSetAtUtc?: string | null;
};

export type CacheOverview = {
  scopes: CacheScopeSummary[];
  totalEntries: number;
};

export type CacheEntry = {
  scope: string;
  moduleCode: string;
  label: string;
  customerId: string;
  cacheKeySuffix: string;
  setAtUtc: string;
};

export type CacheEntryListResult = {
  items: CacheEntry[];
  totalCount: number;
  page: number;
  pageSize: number;
};

export type CacheEntryKey = {
  customerId: string;
  scope: string;
  cacheKeySuffix: string;
};

export type MonitoringLogEntry = {
  id: string;
  timestampUtc: string;
  level: string;
  message: string;
  source?: string | null;
  exceptionType?: string | null;
  exceptionMessage?: string | null;
  stackTrace?: string | null;
  traceId?: string | null;
  requestPath?: string | null;
};

export type MonitoringLogListResult = {
  items: MonitoringLogEntry[];
  totalCount: number;
  page: number;
  pageSize: number;
};

export type MonitoringAuditEntry = {
  id: string;
  timestampUtc: string;
  action: string;
  entityType: string;
  entityId?: string | null;
  userName?: string | null;
  details?: string | null;
};

export type MonitoringAuditListResult = {
  items: MonitoringAuditEntry[];
  totalCount: number;
  page: number;
  pageSize: number;
};

export type MonitoringListQuery = {
  page?: number;
  pageSize?: number;
  search?: string;
  fromUtc?: string;
  toUtc?: string;
  level?: string;
  action?: string;
};

export async function fetchCacheOverview(includeGlobal = false): Promise<CacheOverview> {
  const response = await apiClient.get<CacheOverview>('/v1/monitoring/cache', {
    params: { includeGlobal },
  });
  return response.data;
}

export async function fetchCacheEntries(
  page = 1,
  pageSize = 10,
  scope?: string,
): Promise<CacheEntryListResult> {
  const response = await apiClient.get<CacheEntryListResult>('/v1/monitoring/cache/entries', {
    params: { page, pageSize, scope: scope || undefined },
  });
  return response.data;
}

export async function invalidateCacheScope(scope: string, customerId?: string): Promise<void> {
  await apiClient.post('/v1/monitoring/cache/invalidate', {
    scope,
    customerId: customerId ?? null,
  });
}

export async function removeCacheEntries(entries: CacheEntryKey[]): Promise<number> {
  const response = await apiClient.post<{ removedCount: number }>('/v1/monitoring/cache/entries/remove', {
    entries,
  });
  return response.data.removedCount;
}

export async function clearAllCache(): Promise<number> {
  const response = await apiClient.delete<{ removedCount: number }>('/v1/monitoring/cache/all');
  return response.data.removedCount;
}

export async function fetchMonitoringLogs(
  query: MonitoringListQuery = {},
): Promise<MonitoringLogListResult> {
  const response = await apiClient.get<MonitoringLogListResult>('/v1/monitoring/logs', {
    params: {
      page: query.page ?? 1,
      pageSize: query.pageSize ?? 10,
      search: query.search || undefined,
      fromUtc: query.fromUtc || undefined,
      toUtc: query.toUtc || undefined,
      level: query.level || undefined,
    },
  });
  return response.data;
}

export async function removeMonitoringLogs(ids: string[]): Promise<number> {
  const response = await apiClient.post<{ removedCount: number }>('/v1/monitoring/logs/remove', {
    ids,
  });
  return response.data.removedCount;
}

export async function clearAllMonitoringLogs(): Promise<number> {
  const response = await apiClient.delete<{ removedCount: number }>('/v1/monitoring/logs/all');
  return response.data.removedCount;
}

export async function fetchMonitoringAudit(
  query: MonitoringListQuery = {},
): Promise<MonitoringAuditListResult> {
  const response = await apiClient.get<MonitoringAuditListResult>('/v1/monitoring/audit', {
    params: {
      page: query.page ?? 1,
      pageSize: query.pageSize ?? 10,
      search: query.search || undefined,
      fromUtc: query.fromUtc || undefined,
      toUtc: query.toUtc || undefined,
      action: query.action || undefined,
    },
  });
  return response.data;
}

export async function removeMonitoringAuditEntries(ids: string[]): Promise<number> {
  const response = await apiClient.post<{ removedCount: number }>('/v1/monitoring/audit/remove', {
    ids,
  });
  return response.data.removedCount;
}

export async function clearAllMonitoringAuditEntries(): Promise<number> {
  const response = await apiClient.delete<{ removedCount: number }>('/v1/monitoring/audit/all');
  return response.data.removedCount;
}

export function buildCacheEntryKey(entry: CacheEntry): string {
  return `${entry.scope}:${entry.customerId}:${entry.cacheKeySuffix}`;
}
